/**
 * Parse JSON with error handling
 * @param inputString
 * @returns
 */
export function parseJSON(inputString: string) {
  let parsedJSON: string | JSON;

  try {
    parsedJSON = JSON.parse(inputString);
  } catch (error) {
    parsedJSON = inputString;
  }

  return parsedJSON;
}

interface SafeJsonStringifyOptions {
  /** Pretty print with indentation (default: true) */
  pretty?: boolean;
  /** Indentation spaces when pretty is true (default: 2) */
  indent?: number;
  /** Fallback message when stringification fails (default: '[Unable to serialize]') */
  fallbackMessage?: string;
  /** Maximum length before truncating (optional) */
  maxLength?: number;
}

/**
 * Safely stringify JSON with error handling
 * @param value The value to stringify
 * @param options Stringify options
 * @returns Stringified JSON or fallback message
 */
export function safeJsonStringify(value: any, options: SafeJsonStringifyOptions = {}): string {
  const { pretty = true, indent = 2, fallbackMessage = '[Unable to serialize]', maxLength } = options;

  try {
    const result = JSON.stringify(value, null, pretty ? indent : undefined);
    if (maxLength && result.length > maxLength) {
      return result.substring(0, maxLength) + '...';
    }
    return result;
  } catch (error) {
    // Handle circular references or other JSON.stringify errors
    return fallbackMessage;
  }
}

/**
 * Format a value for display in table cells or UI
 * Handles null, undefined, arrays, objects, and primitive values
 * @param value The value to format
 * @returns Formatted string for display
 */
export function formatValueForDisplay(value: any): string {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return '-';
  }

  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }
    // For arrays of objects, show count and preview
    if (typeof value[0] === 'object') {
      const preview = safeJsonStringify(value[0], {
        pretty: true,
        maxLength: 100,
        fallbackMessage: '[Complex object - unable to preview]',
      });
      return `[${value.length} items] ${preview}`;
    }
    // For simple arrays, show joined values
    return value.join(', ');
  }

  // Handle objects
  if (typeof value === 'object') {
    return safeJsonStringify(value, {
      pretty: true,
      fallbackMessage: '[Complex object - unable to serialize]',
    });
  }

  // Handle primitive values
  return String(value);
}

/**
 * Check if a value can be safely stringified
 * @param value The value to check
 * @returns true if stringifiable, false otherwise
 */
export function isJsonStringifiable(value: any): boolean {
  try {
    JSON.stringify(value);
    return true;
  } catch (error) {
    return false;
  }
}
